import ActionStatus from '@/services/Action/ActionStatus';
import * as permissions from '@/config/permission';
import ActionType from '@/services/Action/ActionType';
import ClientAction from '@/services/Action/ClientAction';
import Action from '@/services/Action/Action';
import Assets from "@/services/Assets/Assets";
import store from '@/store';
import { JOBS_VIEW_JOB_REQUESTS } from '@/config/permission';
import { FEATURE_ACTION_FINDER, isFeatureEnabled } from "@/config/features";


const actionSubNavMeta = {
  subNav: () => import(/* webpackChunkName: "ActionSubNav" */'@/components/Nav/ActionNavbar'),
};
const clientActionSubNavMeta = {
  subNav: () => import(/* webpackChunkName: "ClientActionSubNav" */'@/components/Nav/ClientActionNavbar'),
};
const assetsSubNavMeta = {
  subNav: () => import(/* webpackChunkName: "ClientActionSubNav" */'@/components/Nav/AssetsNavbar'),
};
const propertySubNavMeta = {
  subNav: () => import(/* webpackChunkName: "PropertySubNav" */'@/components/Nav/PropertyNavbar'),
};
const organisationSubNavMeta = {
  subNav: () => import(/* webpackChunkName: "OrganisationSubNav" */'@/components/Nav/OrganisationNavbar'),
};

const analysisMeta = actionSubNavMeta;
const actionMeta = {
  title: 'RA Actions',
  ...actionSubNavMeta,
  model: Action,
};
const clientActionMeta = {
  title: 'Client Actions',
  permissions: [
    {
      action: permissions.ACTIONS_VIEW_CLIENT_ACTIONS,
      subject: permissions.PERMISSION_TYPE_ACTION,
    },
  ],
  ...clientActionSubNavMeta,
  model: ClientAction,
};
const assetsPropertiesMeta = {
  title: 'Assets',
  permissions: [
    {
      action: permissions.ASSETS_VIEW_ASSETS_INVENTORY,
      subject: permissions.PERMISSION_TYPE_ASSETS,
    },
  ],
  ...assetsSubNavMeta,
  model: Assets,
};

const actionRoutes = [];
const assetsRoutes = [];
const propertyRoutes = [];
const jobRoutes = [];

const propertyAssets = {
  path: '/assets/properties/:propertyId',
  redirect: { name: 'assetsProperties' },
};

assetsRoutes.push(propertyAssets);

const actionStatus = {
  path: '/actions/:statusFilter',
  name: 'actionStatus',
  meta: actionMeta,
  beforeEnter: (to, from, next) => {
    const { statusFilter } = to.params;

    if (statusFilter === 'actionFinder') {
      return next();
    }

    const status = ActionStatus.getStatusByRoute(statusFilter);

    if (!status) {
      next({ name: 'dashboard' });
    }

    next();
  },
};

const actionItem = {
  path: '/actions/:clientActionId(\\d+)',
  name: 'actionPage',
  components: {
    default: () => import(/* webpackChunkName: "ActionPage" */ '@/views/pages/Actions/ActionPage.vue'),
    actionPage: () => import(/* webpackChunkName: "ActionPage" */ '@/views/pages/Actions/ActionPage.vue'),
  },
  meta: actionMeta,
};
const actionRedirect = {
  path: '/action/:clientActionId',
  redirect: { name: 'actionPage' },
};
const actionFinder = {
    path: '/actions/finder',
    name: 'actionFinder',
    components: {
        default: () => import(/* webpackChunkName: "ActionFinder" */ '@/views/pages/Actions/Actions.vue'),
    },
    meta: {
      ...actionMeta,
      title: 'Action Finder',
    },
    props: route => ({
      statusFilter: 'actionFinder',
    }),
}

if (!isFeatureEnabled(FEATURE_ACTION_FINDER)) {
    actionFinder.redirect = { name: 'actions' };
}

actionRoutes.push(actionFinder);
actionRoutes.push(actionRedirect);
actionRoutes.push(actionItem);
actionRoutes.push(actionStatus);

const clientActionRoutes = [];

const clientActionStatus = {
  path: '/client-actions/:statusFilter',
  name: 'clientActionStatus',
  meta: clientActionMeta,
  beforeEnter: (to, from, next) => {
    const { statusFilter } = to.params;

    const status = ActionStatus.getStatusByRoute(statusFilter);

    if (!status) {
      if (statusFilter === 'add') {
        next({ name: 'clientActionsAdd' });
      } else {
        next({ name: 'dashboard' });
      }
    }

    next();
  },
};

const clientActionItem = {
  path: '/client-actions/:clientActionId(\\d+)',
  name: 'clientActionPage',
  components: {
    default: () => import(/* webpackChunkName: "ClientActionPage" */ '@/views/pages/Actions/ClientActionPage.vue'),
    clientActionPage: () => import(/* webpackChunkName: "ClientActionPage" */ '@/views/pages/Actions/ClientActionPage.vue'),
  },
  meta: clientActionMeta,
};
const clientActionRedirect = {
  path: '/client-action/:clientActionId',
  redirect: { name: 'clientActionPage' },
};

clientActionRoutes.push(clientActionRedirect);
clientActionRoutes.push(clientActionItem);
clientActionRoutes.push(clientActionStatus);

// Permissions
const appPermissions = [
  {
    action: permissions.APPLICATIONS_CREATE_APP,
    subject: permissions.PERMISSION_TYPE_APPS,
  },
];
const machineAppPermissions = [
  {
    action: permissions.APPLICATIONS_CREATE_MACHINE_APP,
    subject: permissions.PERMISSION_TYPE_APPS,
  },
];
const propertyItem = {
  path: '/programme/properties/:propertyId',
  name: 'property',
  components: {
    default: () => import(/* webpackChunkName: "Property" */ '@/views/pages/Property/Property.vue'),
    property: () => import(/* webpackChunkName: "Property" */ '@/views/pages/Property/Property.vue'),
  },
  meta: {
    title: 'Properties',
    permissions: [
      {
        action: permissions.PROPERTIES_VIEW_PROPERTIES,
        subject: permissions.PERMISSION_TYPE_PROPERTIES,
      },
    ],
    ...propertySubNavMeta,
  },
  props: route => ({
    tab: route.params.tab,
  }),
};

propertyRoutes.push(propertyItem);

const jobItem = {
  path: '/programme/jobs/:jobId',
  name: 'job',
  components: {
    default: () => import(/* webpackChunkName: "Job" */ '@/views/pages/Property/Job.vue'),
    job: () => import(/* webpackChunkName: "Job" */ '@/views/pages/Property/Job.vue'),
  },
  meta: {
    title: 'Jobs',
    permissions: [
      {
        action: permissions.JOBS_VIEW_JOB_REQUESTS,
        subject: permissions.PERMISSION_TYPE_JOBS,
      },
    ],
    ...propertySubNavMeta,
  },
};

jobRoutes.push(jobItem);

export default ({ authGuard, guestGuard }) => [

  // Authenticated routes.
  ...authGuard([
    {
      path: '/dashboard',
      name: 'dashboard',
      redirect: () => {
        //TODO permissions check here
        return { name: 'actions-dashboard' };
      },
      meta: {
        title: 'Dashboard - Riskhub Portal',
      },
      component: () => import(/* webpackChunkName: "Dashboard" */ '@/views/pages/Dashboard.vue'),
    },
    {
      path: '/account',
      name: 'account',
      meta: {
        title: 'Account',
      },
      component: () => import(/* webpackChunkName: "MyAccount" */ '@/views/pages/Account/MyAccount.vue'),
      children: [
        {
          path: '/account/details',
          name: 'details',
          meta: {
            title: 'Account Details',
          },
          component: () => import(/* webpackChunkName: "MyAccount" */ '@/views/pages/Account/Details.vue'),
        },
        {
          path: '/account/api-access',
          name: 'api-access',
          meta: {
            title: 'API Access',
          },
          component: () => import(/* webpackChunkName: "MyAccount" */ '@/views/pages/Account/ApiAccess.vue'),
        },
        {
          path: '/account/apps',
          name: 'apps',
          component: () => import(/* webpackChunkName: "Index_Apps" */ '@/views/pages/Account/Apps/Index.vue'),
          meta: {
            title: 'Apps',
            permissions: appPermissions,
          },
        },
        {
          path: '/account/apps/add',
          name: 'apps-create',
          component: () => import(/* webpackChunkName: "Create_Apps" */ '@/views/pages/Account/Apps/Create.vue'),
          meta: {
            title: 'Apps',
            permissions: appPermissions,
          },
        },
        {
          path: '/account/apps/:id',
          name: 'apps-view',
          component: () => import(/* webpackChunkName: "View_Apps" */ '@/views/pages/Account/Apps/View.vue'),
          meta: {
            title: 'Apps',
            permissions: appPermissions,
          },
        },
        {
          path: '/account/api-credentials',
          name: 'machine-apps',
          component: () => import(/* webpackChunkName: "Index_Machine_Apps" */ '@/views/pages/Account/MachineApps/Index.vue'),
          meta: {
            title: 'API Credentials',
            permissions: machineAppPermissions,
          },
        },
        {
          path: '/account/api-credentials/add',
          name: 'machine-apps-create',
          component: () => import(/* webpackChunkName: "Create_Machine_Apps" */ '@/views/pages/Account/MachineApps/Create.vue'),
          meta: {
            title: 'API Credentials',

            permissions: machineAppPermissions,
          },
        },
        {
          path: '/account/api-credentials/:id',
          name: 'machine-apps-view',
          component: () => import(/* webpackChunkName: "View_Machine_Apps" */ '@/views/pages/Account/MachineApps/View.vue'),
          meta: {
            title: 'API Credentials',
            permissions: machineAppPermissions,
          },
        },
      ],
    },
    {
      path: '/actions/dashboard',
      name: 'actions-dashboard',
      meta: actionMeta,
      components: {
        default: () => import(/* webpackChunkName: "ActionsDashboard" */ '@/views/pages/Actions/ActionsDashboard.vue'),
        actionsDashboard: () => import(/* webpackChunkName: "ActionsDashboard" */ '@/views/pages/Actions/ActionsDashboard.vue'),
      },
    },
    {
      path: '/actions',
      name: 'actions',
      component: () => import(/* webpackChunkName: "Actions" */ '@/views/pages/Actions/Actions.vue'),
      meta: actionMeta,
      props: route => ({
        type: ActionType.getTypeName(ActionType.RISKHUB()),
        statusFilter: ActionStatus.getStatusByRoute(route.params.statusFilter),
        title: route.params.title,
      }),
      children: actionRoutes,
    },
    {
      path: '/assets/dashboard',
      name: 'assets-dashboard',
      meta: assetsPropertiesMeta,
      components: {
        default: () => import(/* webpackChunkName: "AssetsDashboard" */ '@/views/pages/Assets/AssetsDashboard.vue'),
        assetsDashboard: () => import(/* webpackChunkName: "AssetsDashboard" */ '@/views/pages/Assets/AssetsDashboard.vue'),
      },
    },
    {
      path: '/assets/properties',
      name: 'assetsProperties',
      component: () => import(/* webpackChunkName: "AssetsInventory" */ '@/views/pages/Assets/Properties.vue'),
      meta: assetsPropertiesMeta,
      children: assetsRoutes,
    },
    {
      path: '/assets/properties/:propertyId',
      name: 'propertyAssets',
      component: () => import(/* webpackChunkName: "PropertyAssets" */ '@/views/pages/Assets/PropertyAssets.vue'),
      meta: assetsPropertiesMeta,
      children: [],
    },
    {
      path: '/client-actions',
      name: 'clientActions',
      component: () => import(/* webpackChunkName: "ClientActions" */ '@/views/pages/Actions/ClientActions.vue'),
      meta: clientActionMeta,
      props: route => ({
        type: ActionType.getTypeName(ActionType.CLIENT()),
        statusFilter: ActionStatus.getStatusByRoute(route.params.statusFilter),
      }),
      children: clientActionRoutes,
    },
    {
      path: '/client-actions/add',
      name: 'clientActionsAdd',
      component: () => import(/* webpackChunkName: "ClientActionsWizard" */ '@/views/pages/Actions/ClientActions/Add.vue'),
      meta: clientActionMeta,
    },
    {
      path: '/programme/dashboard',
      name: 'property-dashboard',
      meta: {
        title: 'Programme',
        permissions: [
          {
            action: permissions.PROPERTIES_VIEW_PROPERTIES,
            subject: permissions.PERMISSION_TYPE_PROPERTIES,
          },
        ],
        ...propertySubNavMeta,
      },
      components: {
        default: () => import(/* webpackChunkName: "ActionsDashboard" */ '@/views/pages/Property/PropertyDashboard.vue'),
        propertyDashboard: () => import(/* webpackChunkName: "ActionsDashboard" */ '@/views/pages/Property/PropertyDashboard.vue'),
      },
    },
    {
      path: '/programme/jobs',
      name: 'jobs',
      component: () => import(/* webpackChunkName: "Jobs" */ '@/views/pages/Property/Jobs.vue'),
      meta: {
        title: 'Jobs',
        permissions: [
          {
            action: permissions.JOBS_VIEW_JOB_REQUESTS,
            subject: permissions.PERMISSION_TYPE_JOBS,
          },
        ],
        ...propertySubNavMeta,
      },
      children: jobRoutes,
    },
    {
      path: '/programme/management',
      name: 'programme',
      component: () => import(/* webpackChunkName: "NewAndRenewals" */ '@/views/pages/Property/NewAndRenewals.vue'),
      meta: {
        title: 'Management',
        permissions: [
          {
            action: permissions.JOBS_VIEW_JOB_REQUESTS,
            subject: permissions.PERMISSION_TYPE_JOBS,
          },
        ],
        ...propertySubNavMeta,
      },
      props: route => ({
        title: route.params.title,
      }),
    },
    {
      path: '/programme',
      name: 'property-manager',
      redirect: function() {
        if (store.getters.permissions.includes(JOBS_VIEW_JOB_REQUESTS)) {
          return '/programme/dashboard';
        }
        return '/programme/properties';
      },
      meta: {
        permissions: [
          {
            action: permissions.PROPERTIES_VIEW_PROPERTIES,
            subject: permissions.PERMISSION_TYPE_PROPERTIES,
          },
        ],
      },
    },
    {
      path: '/programme/properties',
      name: 'properties',
      component: () => import(/* webpackChunkName: "Properties" */ '@/views/pages/Property/Properties.vue'),
      meta: {
        title: 'List',
        permissions: [
          {
            action: permissions.PROPERTIES_VIEW_PROPERTIES,
            subject: permissions.PERMISSION_TYPE_PROPERTIES,
          },
        ],
        ...propertySubNavMeta,
      },
      children: propertyRoutes,
    },
    {
      path: '/programme/hand-ins',
      name: 'hand-ins',
      component: () => import(/* webpackChunkName: "HandIns" */ '@/views/pages/Property/HandIns.vue'),
      meta: {
        title: 'Hand-Ins',
        permissions: [
          {
            action: permissions.JOBS_VIEW_HAND_INS,
            subject: permissions.PERMISSION_TYPE_JOBS,
          },
        ],
        ...propertySubNavMeta,
      },
    },
    {
      path: '/organisation',
      name: 'org',
      redirect: '/organisation/chart',
      meta: {
        title: 'Organisation',
        permissions: [
          {
            action: permissions.ADMIN_ORGANISATION_HIERARCHY,
            subject: permissions.PERMISSION_TYPE_ADMIN,
          },
        ],
      },
    },
    {
      path: '/organisation/chart',
      name: 'orgChart',
      component: () => import(/* webpackChunkName: "OrganisationChart" */ '@/views/pages/Organisation/Chart.vue'),
      meta: {
        title: 'Organisation Chart',
        permissions: [
          {
            action: permissions.ADMIN_ORGANISATION_HIERARCHY,
            subject: permissions.PERMISSION_TYPE_ADMIN,
          },
        ],
        ...organisationSubNavMeta,
      },
    },
    {
      path: '/organisation/users',
      name: 'users',
      component: () => import(/* webpackChunkName: "Users" */ '@/views/pages/Organisation/Users.vue'),
      meta: {
        permissions: [
          {
            action: permissions.ADMIN_USER_MANAGEMENT,
            subject: permissions.PERMISSION_TYPE_ADMIN,
          },
        ],
        ...organisationSubNavMeta,
      },
    },
    {
      path: '/organisation/users/add',
      name: 'usersAdd',
      component: () => import(/* webpackChunkName: "UserWizard" */ '@/views/pages/Organisation/Users/Add.vue'),
      meta: organisationSubNavMeta,
    },
    {
      path: '/eula',
      name: 'eula',
      meta: {
        title: 'EULA',
      },
      component: () => import(/* webpackChunkName: "EULA" */ '@/views/pages/EULA/EULA.vue'),
    },
    {
      path: '/actions/analysis',
      name: 'analysis',
      component: () => import(/* webpackChunkName: "Analysis" */ '@/views/pages/Analysis/Analysis.vue'),
      meta: {
        title: 'Analysis',
        ...analysisMeta
      },
    },
    {
      path: '/actions/work-plans',
      name: 'work-plans',
      component: () => import(/* webpackChunkName: "WorkPlans" */ '@/views/pages/Actions/WorkPlans.vue'),
      meta: {
        title: 'Work Plans',
        permissions: [
          {
            action: permissions.ACTIONS_MANAGE_WORK_PLANS,
            subject: permissions.PERMISSION_TYPE_ACTION,
          },
        ],
        ...analysisMeta
      },
    },
    {
      path: '/download/:clientUserDownloadId',
      name: 'download',
      component: () => import(/* webpackChunkName: "Download" */ '@/views/pages/Download/Download.vue'),
      meta: {},
    },
    {
      path: '/file-sharing',
      name: 'file-sharing',
      component: () => import(/* webpackChunkName: "Files" */ '@/views/pages/Files/Files.vue'),
      meta: {
        title: 'File Sharing',
        permissions: [
          {
            action: permissions.FILES_FILE_SHARING,
            subject: permissions.PERMISSION_TYPE_FILES,
          },
        ],
      },
    },
    {
      path: '/team-management',
      name: 'team-management',
      meta: {
        title: 'Team Management',
      },
      component: () => import(/* webpackChunkName: "TeamManagement" */ '@/views/pages/TeamManagement.vue'),
    },
  ]),

  // Guest routes.
  ...guestGuard([
    {
      path: '/',
      name: 'home',
      component: () => import(/* webpackChunkName: "Home" */ '@/views/pages/Home.vue'),
      meta: { layout: 'home' },
    },
  ]),
  { path: '*', redirect: { name: 'home' } },
  {
    path: '/login',
    name: 'login',
    component: () => import(/* webpackChunkName: "Login" */ '@/views/pages/Auth/Login.vue'),
    meta: { layout: 'login' },
  },
];
